import { Signal } from "../core/Signal.js";
import { AbstractLoader, AbstractLoaderOptions, LoaderProgress } from "./AbstractLoader.js";
import { Decoder, decoders, LoaderDecoderMap } from "./Decoder.js";
import { Encoder, encoders, LoaderEncoderMap } from "./Encoder.js";

export type AjaxLoaderData = Document | BodyInit | null | any;

export type AjaxLoadMethod = "GET" | "POST" | "PUT" | "DELETE" | "HEAD" | "AUTO";

export interface AjaxLoaderOptions extends AbstractLoaderOptions {
    method?: AjaxLoadMethod;
    data?: AjaxLoaderData;
    user?: string;
    password?: string;
    decoder?: keyof LoaderDecoderMap | null;
    encoder?: keyof LoaderEncoderMap | null;
    onUpload?: (evt: LoaderProgress) => void;
}

export abstract class AbstractAjaxLoader<Response, Options extends AjaxLoaderOptions> extends AbstractLoader<Response, Options>{
    protected _ajax: XMLHttpRequest = null;
    protected _onUpload = Signal.create<[LoaderProgress]>();
    protected _decoder: Decoder = null;
    protected _encoder: Encoder = null;

    constructor(url: string, options: Options = null) {
        super(url, options);

        if (this._options.decoder) {
            this._decoder = decoders[this._options.decoder];
        }

        if (this._options.encoder) {
            this._encoder = encoders[this._options.encoder];
        }

        if (typeof this._options.onUpload === "function") {
            this._onUpload.add(this._options.onUpload);
        }

        this.create(url);
    }

    private async create(url: string): Promise<any> {

        this._ajax = new XMLHttpRequest();

        this._ajax.onload = async (e) => {
            var data = this._ajax.response;
            if (this._decoder && typeof this._decoder.decode === "function") {
                data = await this._decoder.decode(this._ajax);
            }

            if (typeof this.convert === "function") {
                this.resolve(await this.convert(data));
            } else {
                this.resolve(data);
            }
        }

        this._ajax.onerror = (e) => {
            this.reject(e);
        }

        let lastLoaded = 0;
        this._ajax.onprogress = (e: ProgressEvent) => {
            var total = e.total;
            if (!total) {
                try {
                    total = parseInt(this._ajax.getResponseHeader('x-decompressed-content-length'));
                } catch (error) {
                    // console.warn(error);
                }
            }
            if (isNaN(total)){
                total = 0;
            }
            
            this._onProgress.dispatch({
                event: e,
                delta: e.loaded - lastLoaded,
                loaded: e.loaded,
                total: total
            });
            lastLoaded = e.loaded;
        }

        this._ajax.upload.onprogress = (e: ProgressEvent) => {
            this._onUpload.dispatch({
                event: e,
                loaded: e.loaded,
                delta: e.loaded - lastLoaded,
                total: e.total
            });
            lastLoaded = e.loaded;
        }

        var method: AjaxLoadMethod = this._options.method;
        if (!method)
            method = "AUTO";
        if (method === "AUTO") {
            method = this._options.data ? "POST" : "GET";
        }

        this._ajax.open(method, url, true, this._options.user, this._options.password);

        var data = this._options.data;
        if (data && this._encoder) {
            data = await this._encoder.encode(this._ajax, data);
        }

        if (this._decoder && this._decoder.responseType !== undefined) {
            this._ajax.responseType = this._decoder.responseType;
        }

        this._ajax.send(data);
    }

    public cancel(reason: any = null): this {
        this._ajax.onerror = null;
        this._ajax.abort();
        super.cancel(reason);
        return this;
    }

    protected finish(): void {
        this._onUpload.dispose();
        this._onUpload = null;
        this._ajax.onload = null;
        this._ajax.onerror = null;
        this._ajax.onprogress = null;
        this._ajax.upload.onprogress = null;
        this._ajax = null;
        this._encoder = null;
        this._decoder = null;

        super.finish();
    }

    protected async convert?(data: any): Promise<Response>;
}